import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './AccountList.module.scss';
import classNames from 'classnames/bind';
import logo from '~/assets/images/logo.png';
import { BaseImage } from '~/components/common';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { accountActions } from '~/thunks/account/accountSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/accounts/menu-option/MenuOption';
import { IAccount, IAccountList } from '~/utils/interface/account';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type AccountListProps = {
  data?: IAccount;
  onOpenEdit: (id: string) => void;
  onOpenDrawer: (id: string) => void;
};

function AccountList(props: AccountListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/account']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('user'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'roles',
        label: t('roles'),
        sortable: false,
        searchable: false,
      },
      {
        id: 'email',
        label: t('email'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'department',
        label: t('department'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'phone',
        label: t('phoneNumber'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'address',
        label: t('address'),
        sortable: true,
        searchable: true,
      },
      {
        id: '',
        label: '',
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { accounts, filter, currentPage, totalPages } = useAppSelector((state) => state.account);
  const dispatch = useAppDispatch();
  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(accountActions.changeAccountId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(accountActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(accountActions.changeMenuSelectId(menuSelectedId));
      dispatch(accountActions.changeOpenAlertDelete(true));
    }
  };
  const handleOpenDrawer = () => {
    if (menuSelectedId !== undefined) {
      props.onOpenDrawer(menuSelectedId);
    }
    handleClose();
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      accountActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      accountActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  return (
    <Box className={cx('account-list-container')}>
      <BaseTable<IAccountList>
        data={accounts}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(account: IAccountList) => {
          return (
            <>
              <TableCell>
                <Box className={cx('account-container')}>
                  <BaseImage src={account.avatar ?? logo} className={cx('image')} />
                  <Box className={cx('account-name-container')}>
                    <Box className={cx('accountName')}>{account.name}</Box>
                  </Box>
                </Box>
              </TableCell>
              <TableCell className='w-[300px]'>
                <Box className='flex gap-[5px] flex-wrap w-[200px]'>
                  {account?.roles?.map(({ name }) => (
                    <Chip label={name} size='small' />
                  ))}
                </Box>
              </TableCell>
              <TableCell>{account.email}</TableCell>
              <TableCell>
                <Box className={cx('deparment-container')}>
                  <Box className={cx('department-info')}>{t(`departmentName.${account.department}`)}</Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box className={cx('phonenumber-container')}>{account.phone}</Box>
              </TableCell>

              <TableCell>
                <Box className={cx('address-container')}>{account.address}</Box>
              </TableCell>

              {(permissions[PERMISSIONS.account_edit] || permissions[PERMISSIONS.account_delete]) && (
                <TableCell>
                  <Box className={cx('deadline-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, account.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        handleOpenDrawer={handleOpenDrawer}
        enableEdit={permissions[PERMISSIONS.account_edit]}
        enableDelete={permissions[PERMISSIONS.account_delete]}
      />
    </Box>
  );
}

export default memo(AccountList);
